// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Save,
  Mail,
  FileText,
  Receipt,
  Bell,
  Plus,
  Edit2,
  Eye,
  Trash2,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { confirm } from '@/stores/confirmStore';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { emailTemplatesAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { useReminderPolicies } from '@/hooks/useReminderPolicies';
import type { ReminderLevel } from '@/hooks/useReminderPolicies';

interface EmailTemplate {
  subject: string;
  body: string;
}

interface FullEmailTemplate {
  id: number;
  companyId: number | null;
  templateType: string;
  templateName: string;
  subjectTemplate: string;
  bodyTemplate: string;
  htmlTemplate: string | null;
  availableVariables: string[];
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const EmailTemplates: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<'quotes' | 'invoices' | 'reminders'>(
    'quotes'
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ✅ Hooks ReminderPolicies (travaillent directement avec ReminderPolicy)
  const invoicePolicy = useReminderPolicies('invoice');
  const quotePolicy = useReminderPolicies('quote');

  // États pour les templates complets (devis et factures)
  const [quoteTemplates, setQuoteTemplates] = useState<FullEmailTemplate[]>([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState<FullEmailTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<Partial<FullEmailTemplate> | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'edit' | 'preview'>('list');
  const [previewTemplate, setPreviewTemplate] = useState<FullEmailTemplate | null>(null);

  // États pour les sections ouvertes/fermées
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    'invoice-first': false,
    'invoice-second': false,
    'invoice-third': false,
    'quote-first': false,
    'quote-second': false,
    'quote-third': false,
  });

  // Card classes V2
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await emailTemplatesAPI.getAll();
      const templates = data.templates || [];

      setQuoteTemplates(templates.filter((t: FullEmailTemplate) => t.templateType === 'quote'));
      setInvoiceTemplates(templates.filter((t: FullEmailTemplate) => t.templateType === 'invoice'));
    } catch (error) {
      notify.error(t('templates.email.notify.loadError'), t('templates.email.notify.errorTitle'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = (type: 'quote' | 'invoice') => {
    const variables =
      type === 'quote'
        ? [
            'quoteNumber',
            'clientName',
            'companyName',
            'totalHt',
            'totalTtc',
            'validUntil',
            'currencyCode',
          ]
        : [
            'invoiceNumber',
            'clientName',
            'companyName',
            'totalHt',
            'totalTtc',
            'issueDate',
            'dueDate',
            'currencyCode',
            'iban',
            'bic',
          ];

    setEditingTemplate({
      templateType: type,
      templateName: '',
      subjectTemplate: '',
      bodyTemplate: '',
      htmlTemplate: '',
      availableVariables: variables,
      isDefault: false,
      isActive: true,
    });
    setViewMode('edit');
  };

  const handleEdit = (template: FullEmailTemplate) => {
    setEditingTemplate({ ...template });
    setViewMode('edit');
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    if (
      !editingTemplate.templateName ||
      !editingTemplate.subjectTemplate ||
      !editingTemplate.bodyTemplate
    ) {
      notify.warning(t('templates.email.notify.requiredFields'), t('templates.email.notify.requiredFieldsTitle'));
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate.id) {
        await emailTemplatesAPI.update(String(editingTemplate.id), editingTemplate);
        notify.success(t('templates.email.notify.updated'), t('templates.email.notify.successTitle'));
      } else {
        await emailTemplatesAPI.create(editingTemplate);
        notify.success(t('templates.email.notify.created'), t('templates.email.notify.successTitle'));
      }

      await loadTemplates();
      setViewMode('list');
      setEditingTemplate(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('templates.email.notify.connectionError');
      notify.error(message, t('templates.email.notify.errorTitle'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template: FullEmailTemplate) => {
    const confirmed = await confirm({
      title: t('templates.email.confirm.deleteTitle'),
      message: t('templates.email.confirm.deleteMessage', { name: template.templateName }),
      confirmText: t('templates.email.confirm.delete'),
      cancelText: t('templates.email.confirm.cancel'),
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await emailTemplatesAPI.delete(String(template.id));
      notify.success(t('templates.email.notify.deleted'), t('templates.email.notify.successTitle'));
      await loadTemplates();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('templates.email.notify.connectionError');
      notify.error(message, t('templates.email.notify.errorTitle'));
    }
  };

  const handlePreview = (template: FullEmailTemplate) => {
    setPreviewTemplate(template);
    setViewMode('preview');
  };

  const replaceVariables = (text: string, data: Record<string, string>): string => {
    let result = text;
    Object.keys(data).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, data[key]);
    });
    return result;
  };

  const insertVariable = (variable: string) => {
    if (!editingTemplate) return;
    setEditingTemplate((prev) => ({
      ...prev,
      bodyTemplate: (prev?.bodyTemplate || '') + `{{${variable}}}`,
    }));
  };

  // ============================================
  // GESTION DES REMINDER POLICIES
  // ============================================

  const updateInvoiceLevel = (levelIndex: number, updates: Partial<ReminderLevel>) => {
    if (!invoicePolicy.currentPolicy) return;

    const newLevels = [...invoicePolicy.currentPolicy.levels];
    newLevels[levelIndex] = { ...newLevels[levelIndex], ...updates };

    invoicePolicy.updateCurrentPolicy({ levels: newLevels });
  };

  const updateQuoteLevel = (levelIndex: number, updates: Partial<ReminderLevel>) => {
    if (!quotePolicy.currentPolicy) return;

    const newLevels = [...quotePolicy.currentPolicy.levels];
    newLevels[levelIndex] = { ...newLevels[levelIndex], ...updates };

    quotePolicy.updateCurrentPolicy({ levels: newLevels });
  };

  const saveInvoiceReminderSettings = async () => {
    if (!invoicePolicy.currentPolicy) return;

    setSaving(true);
    try {
      await invoicePolicy.updatePolicy(invoicePolicy.currentPolicy.id, {
        isActive: invoicePolicy.currentPolicy.isActive,
        levels: invoicePolicy.currentPolicy.levels,
        sendCopyToCompany: invoicePolicy.currentPolicy.sendCopyToCompany,
        autoMarkOverdue: invoicePolicy.currentPolicy.autoMarkOverdue,
      });

      notify.success(t('templates.email.notify.invoiceRemindersSaved'), t('templates.email.notify.successTitle'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('templates.email.notify.connectionError');
      notify.error(message, t('templates.email.notify.errorTitle'));
    } finally {
      setSaving(false);
    }
  };

  const saveQuoteReminderSettings = async () => {
    if (!quotePolicy.currentPolicy) return;

    setSaving(true);
    try {
      await quotePolicy.updatePolicy(quotePolicy.currentPolicy.id, {
        isActive: quotePolicy.currentPolicy.isActive,
        levels: quotePolicy.currentPolicy.levels,
        sendCopyToCompany: quotePolicy.currentPolicy.sendCopyToCompany,
        stopOnExpiry: quotePolicy.currentPolicy.stopOnExpiry,
      });

      notify.success(t('templates.email.notify.quoteRemindersSaved'), t('templates.email.notify.successTitle'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('templates.email.notify.connectionError');
      notify.error(message, t('templates.email.notify.errorTitle'));
    } finally {
      setSaving(false);
    }
  };

  const renderTemplateEditor = (
    level: ReminderLevel,
    onChange: (updates: Partial<ReminderLevel>) => void,
    variables: string[],
    levelName: 'first' | 'second' | 'third',
    sectionKey: string
  ) => {
    const isOpen = openSections[sectionKey] || false;
    const setIsOpen = (open: boolean) => {
      setOpenSections((prev) => ({ ...prev, [sectionKey]: open }));
    };
    const levelInfo = {
      first: { label: t('templates.email.reminders.level1'), color: 'warning', description: t('templates.email.reminders.friendlyReminder') },
      second: { label: t('templates.email.reminders.level2'), color: 'warning', description: t('templates.email.reminders.firmReminder') },
      third: { label: t('templates.email.reminders.level3'), color: 'alert', description: t('templates.email.reminders.lastWarning') },
    };

    const info = levelInfo[levelName];
    const template = level.emailTemplate || { subject: '', body: '' };

    return (
      <div className="border-b border-white/20 last:border-b-0">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/20 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Badge variant={info.color as 'warning' | 'alert'}>{info.label}</Badge>
            <span className="text-sm text-gray-600">{info.description}</span>
          </div>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isOpen && (
          <div className="px-4 pb-4 space-y-3 bg-white/10">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.email.reminders.emailSubject')}
              </label>
              <input
                type="text"
                value={template.subject}
                onChange={(e) =>
                  onChange({
                    emailTemplate: { ...template, subject: e.target.value },
                  })
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.email.reminders.emailBody')}
              </label>
              <textarea
                value={template.body}
                onChange={(e) =>
                  onChange({
                    emailTemplate: { ...template, body: e.target.value },
                  })
                }
                rows={8}
                className={`${inputClasses} font-mono`}
              />
              <div className="mt-2 p-3 bg-teal-50/50 border border-teal-200/50 rounded-xl">
                <p className="text-xs font-medium text-teal-900 mb-1">{t('templates.email.reminders.availableVariables')}:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-teal-700">
                  {variables.map((v, i) => (
                    <code key={i}>{v}</code>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Mail className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('templates.email.title')}</h1>
          <p className="text-gray-500 mt-0.5">
            {t('templates.email.subtitle')}
          </p>
        </div>
      </div>

      {/* Navigation par catégories */}
      <div className={`${cardClasses} p-2`}>
        <nav className="flex">
          <button
            onClick={() => setActiveCategory('quotes')}
            className={`flex-1 py-3 px-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
              activeCategory === 'quotes'
                ? 'bg-teal-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            <FileText className="w-4 h-4" />
            {t('templates.email.tabs.quotes')}
          </button>
          <button
            onClick={() => setActiveCategory('invoices')}
            className={`flex-1 py-3 px-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
              activeCategory === 'invoices'
                ? 'bg-teal-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            <Receipt className="w-4 h-4" />
            {t('templates.email.tabs.invoices')}
          </button>
          <button
            onClick={() => setActiveCategory('reminders')}
            className={`flex-1 py-3 px-4 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
              activeCategory === 'reminders'
                ? 'bg-teal-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-white/50'
            }`}
          >
            <Bell className="w-4 h-4" />
            {t('templates.email.tabs.reminders')}
          </button>
        </nav>
      </div>

      {/* Contenu */}
      {/* Catégorie Devis */}
      {activeCategory === 'quotes' && viewMode === 'list' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4 flex-1">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-teal-100 rounded-xl">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div className="text-sm text-teal-800">
                  <p className="font-semibold mb-1">{t('templates.email.quoteTemplates')}</p>
                  <p className="text-xs">
                    {t('templates.email.quoteTemplatesDescription')}
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => handleCreateNew('quote')}
              icon={<Plus className="w-4 h-4" />}
            >
              {t('templates.email.newTemplate')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quoteTemplates.map((template) => (
              <div key={template.id} className={cardClasses}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{template.templateName}</h3>
                    <p className="text-sm text-gray-500">{t('templates.email.quoteTemplate')}</p>
                  </div>
                  {template.isDefault && <Badge variant="success">{t('templates.email.default')}</Badge>}
                </div>

                <div className="text-sm text-gray-600 mb-4">
                  <p className="truncate">
                    <strong>{t('templates.email.subject')}:</strong> {template.subjectTemplate}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePreview(template)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-xl transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    {t('templates.email.view')}
                  </button>
                  <button
                    onClick={() => handleEdit(template)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('templates.email.edit')}
                  </button>
                  {template.companyId && (
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {quoteTemplates.length === 0 && (
            <div className={cardClasses}>
              <div className="text-center py-8">
                <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                  <Mail className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('templates.email.emptyQuote')}
                </h3>
                <p className="text-gray-600">{t('templates.email.emptyDescription')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Catégorie Factures */}
      {activeCategory === 'invoices' && viewMode === 'list' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div className="bg-green-50/30 backdrop-blur-sm border border-green-200/50 rounded-2xl p-4 flex-1">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <Receipt className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-1">{t('templates.email.invoiceTemplates')}</p>
                  <p className="text-xs">
                    {t('templates.email.invoiceTemplatesDescription')}
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => handleCreateNew('invoice')}
              icon={<Plus className="w-4 h-4" />}
            >
              {t('templates.email.newTemplate')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {invoiceTemplates.map((template) => (
              <div key={template.id} className={cardClasses}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{template.templateName}</h3>
                    <p className="text-sm text-gray-500">{t('templates.email.invoiceTemplate')}</p>
                  </div>
                  {template.isDefault && <Badge variant="success">{t('templates.email.default')}</Badge>}
                </div>

                <div className="text-sm text-gray-600 mb-4">
                  <p className="truncate">
                    <strong>{t('templates.email.subject')}:</strong> {template.subjectTemplate}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePreview(template)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-xl transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    {t('templates.email.view')}
                  </button>
                  <button
                    onClick={() => handleEdit(template)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    {t('templates.email.edit')}
                  </button>
                  {template.companyId && (
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {invoiceTemplates.length === 0 && (
            <div className={cardClasses}>
              <div className="text-center py-8">
                <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                  <Mail className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('templates.email.emptyInvoice')}
                </h3>
                <p className="text-gray-600">{t('templates.email.emptyDescription')}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Catégorie Relances */}
      {activeCategory === 'reminders' && (
        <div className="space-y-6">
          {/* État vide - Relances Factures */}
          {!invoicePolicy.currentPolicy && !invoicePolicy.loading && (
            <div className={cardClasses}>
              <div className="text-center py-8">
                <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                  <Receipt className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('templates.email.reminders.emptyInvoice')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('templates.email.reminders.emptyInvoiceDescription')}
                </p>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    try {
                      await invoicePolicy.getOrCreatePolicy();
                      notify.success(t('templates.email.notify.invoiceRemindersCreated'));
                    } catch (error) {
                      logger.error('Erreur création policy factures', error);
                      notify.error(t('templates.email.notify.reminderCreationError'));
                    }
                  }}
                  icon={<Plus className="w-4 h-4" />}
                >
                  {t('templates.email.reminders.createInvoice')}
                </Button>
              </div>
            </div>
          )}

          {/* Relances Factures */}
          {invoicePolicy.currentPolicy && (
            <div className={`${cardClasses} p-0 overflow-hidden`}>
              <div className="px-6 py-4 border-b border-white/30 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-green-600" />
                    {t('templates.email.reminders.invoiceTitle')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('templates.email.reminders.invoiceDescription')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={saveInvoiceReminderSettings}
                  disabled={saving}
                  icon={<Save className="w-4 h-4" />}
                >
                  {saving ? t('templates.common.saving') : t('templates.common.save')}
                </Button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="invoice-reminder-enabled"
                    checked={invoicePolicy.currentPolicy.isActive}
                    onChange={(e) =>
                      invoicePolicy.updateCurrentPolicy({ isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <label
                    htmlFor="invoice-reminder-enabled"
                    className="text-sm font-medium text-gray-900"
                  >
                    {t('templates.email.reminders.enableInvoice')}
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  {invoicePolicy.currentPolicy.levels.map((level, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('templates.email.reminders.levelDaysLabel', { ordinal: idx === 0 ? t('templates.email.reminders.first') : idx === 1 ? t('templates.email.reminders.second') : t('templates.email.reminders.third') })}
                      </label>
                      <input
                        type="number"
                        value={level.daysAfter}
                        onChange={(e) =>
                          updateInvoiceLevel(idx, { daysAfter: parseInt(e.target.value) })
                        }
                        className={inputClasses}
                        min="1"
                      />
                    </div>
                  ))}
                </div>

                <div className="bg-white/30 rounded-2xl overflow-hidden border border-white/30">
                  <div className="bg-white/30 px-4 py-3 border-b border-white/30">
                    <h4 className="text-sm font-medium text-gray-900">{t('templates.email.reminders.emailTemplates')}</h4>
                  </div>
                  <div>
                    {invoicePolicy.currentPolicy.levels.map((level, idx) => {
                      const levelName = idx === 0 ? 'first' : idx === 1 ? 'second' : 'third';
                      const sectionKey = `invoice-${levelName}`;

                      return (
                        <div key={idx}>
                          {renderTemplateEditor(
                            level,
                            (updates) => updateInvoiceLevel(idx, updates),
                            [
                              t('templates.email.reminders.invoiceVars.invoiceNumber'),
                              t('templates.email.reminders.invoiceVars.amount'),
                              t('templates.email.reminders.invoiceVars.dueDate'),
                              t('templates.email.reminders.invoiceVars.daysOverdue'),
                              t('templates.email.reminders.invoiceVars.clientName'),
                              t('templates.email.reminders.invoiceVars.companyName'),
                            ],
                            levelName as 'first' | 'second' | 'third',
                            sectionKey
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* État vide - Relances Devis */}
          {!quotePolicy.currentPolicy && !quotePolicy.loading && (
            <div className={cardClasses}>
              <div className="text-center py-8">
                <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                  <FileText className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('templates.email.reminders.emptyQuote')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('templates.email.reminders.emptyQuoteDescription')}
                </p>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    try {
                      await quotePolicy.getOrCreatePolicy();
                      notify.success(t('templates.email.notify.quoteRemindersCreated'));
                    } catch (error) {
                      logger.error('Erreur création policy devis', error);
                      notify.error(t('templates.email.notify.reminderCreationError'));
                    }
                  }}
                  icon={<Plus className="w-4 h-4" />}
                >
                  {t('templates.email.reminders.createQuote')}
                </Button>
              </div>
            </div>
          )}

          {/* Relances Devis */}
          {quotePolicy.currentPolicy && (
            <div className={`${cardClasses} p-0 overflow-hidden`}>
              <div className="px-6 py-4 border-b border-white/30 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-600" />
                    {t('templates.email.reminders.quoteTitle')}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('templates.email.reminders.quoteDescription')}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={saveQuoteReminderSettings}
                  disabled={saving}
                  icon={<Save className="w-4 h-4" />}
                >
                  {saving ? t('templates.common.saving') : t('templates.common.save')}
                </Button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="quote-reminder-enabled"
                    checked={quotePolicy.currentPolicy.isActive}
                    onChange={(e) =>
                      quotePolicy.updateCurrentPolicy({ isActive: e.target.checked })
                    }
                    className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <label
                    htmlFor="quote-reminder-enabled"
                    className="text-sm font-medium text-gray-900"
                  >
                    {t('templates.email.reminders.enableQuote')}
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  {quotePolicy.currentPolicy.levels.map((level, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('templates.email.reminders.levelDaysLabel', { ordinal: idx === 0 ? t('templates.email.reminders.first') : idx === 1 ? t('templates.email.reminders.second') : t('templates.email.reminders.third') })}
                      </label>
                      <input
                        type="number"
                        value={level.daysAfter}
                        onChange={(e) =>
                          updateQuoteLevel(idx, { daysAfter: parseInt(e.target.value) })
                        }
                        className={inputClasses}
                        min="1"
                      />
                    </div>
                  ))}
                </div>

                <div className="bg-white/30 rounded-2xl overflow-hidden border border-white/30">
                  <div className="bg-white/30 px-4 py-3 border-b border-white/30">
                    <h4 className="text-sm font-medium text-gray-900">{t('templates.email.reminders.emailTemplates')}</h4>
                  </div>
                  <div>
                    {quotePolicy.currentPolicy.levels.map((level, idx) => {
                      const levelName = idx === 0 ? 'first' : idx === 1 ? 'second' : 'third';
                      const sectionKey = `quote-${levelName}`;

                      return (
                        <div key={idx}>
                          {renderTemplateEditor(
                            level,
                            (updates) => updateQuoteLevel(idx, updates),
                            [
                              t('templates.email.reminders.quoteVars.quoteNumber'),
                              t('templates.email.reminders.quoteVars.amount'),
                              t('templates.email.reminders.quoteVars.createdAt'),
                              t('templates.email.reminders.quoteVars.expiryDate'),
                              t('templates.email.reminders.quoteVars.daysSinceCreated'),
                              t('templates.email.reminders.quoteVars.daysUntilExpiry'),
                              t('templates.email.reminders.quoteVars.clientName'),
                              t('templates.email.reminders.quoteVars.companyName'),
                            ],
                            levelName as 'first' | 'second' | 'third',
                            sectionKey
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vue Édition */}
      {viewMode === 'edit' && editingTemplate && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {editingTemplate.id ? t('templates.email.editor.editTitle') : t('templates.email.editor.newTitle')}
            </h2>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setViewMode('list');
                  setEditingTemplate(null);
                }}
                icon={<X className="w-4 h-4" />}
              >
                {t('templates.common.cancel')}
              </Button>
              <Button
                variant="secondary"
                onClick={handleSave}
                disabled={saving}
                icon={<Save className="w-4 h-4" />}
              >
                {saving ? t('templates.common.saving') : t('templates.email.editor.save')}
              </Button>
            </div>
          </div>

          <div className={`${cardClasses} space-y-4`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.email.editor.templateName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingTemplate.templateName || ''}
                onChange={(e) =>
                  setEditingTemplate((prev) => ({ ...prev, templateName: e.target.value }))
                }
                className={inputClasses}
                placeholder={t('templates.email.editor.templateNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.email.editor.emailSubject')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingTemplate.subjectTemplate || ''}
                onChange={(e) =>
                  setEditingTemplate((prev) => ({ ...prev, subjectTemplate: e.target.value }))
                }
                className={inputClasses}
                placeholder={t('templates.email.editor.emailSubjectPlaceholder')}
              />
            </div>

            {editingTemplate.availableVariables && editingTemplate.availableVariables.length > 0 && (
              <div className="bg-teal-50/50 border border-teal-200/50 rounded-2xl p-4">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-teal-900">{t('templates.email.editor.availableVariables')}</h4>
                    <p className="text-sm text-teal-700 mt-1">
                      {t('templates.email.editor.availableVariablesHelp')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.availableVariables.map((variable) => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="px-3 py-1.5 bg-white border border-teal-300 text-teal-700 rounded-xl text-sm hover:bg-teal-100 transition-colors"
                    >
                      {'{{'}
                      {variable}
                      {'}}'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('templates.email.editor.bodyText')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={editingTemplate.bodyTemplate || ''}
                onChange={(e) =>
                  setEditingTemplate((prev) => ({ ...prev, bodyTemplate: e.target.value }))
                }
                rows={8}
                className={`${inputClasses} font-mono`}
                placeholder={t('templates.email.editor.bodyPlaceholder')}
              />
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTemplate.isDefault || false}
                  onChange={(e) =>
                    setEditingTemplate((prev) => ({ ...prev, isDefault: e.target.checked }))
                  }
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('templates.email.editor.defaultTemplate')}</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTemplate.isActive !== false}
                  onChange={(e) =>
                    setEditingTemplate((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('templates.email.editor.active')}</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Vue Prévisualisation */}
      {viewMode === 'preview' && previewTemplate && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('templates.email.preview.title')}</h2>
              <p className="text-sm text-gray-500 mt-1">{previewTemplate.templateName}</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setViewMode('list');
                setPreviewTemplate(null);
              }}
              icon={<X className="w-4 h-4" />}
            >
              {t('templates.common.close')}
            </Button>
          </div>

          <div className={`${cardClasses} space-y-4`}>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('templates.email.preview.subject')}</h3>
              <div className="bg-white/50 p-4 rounded-2xl border border-white/50">
                {replaceVariables(previewTemplate.subjectTemplate, {
                  quoteNumber: 'DEV-2025-001',
                  invoiceNumber: 'FAC-2025-001',
                  clientName: 'Jean Dupont',
                  companyName: 'Ma Société',
                  totalHt: '1 000,00 €',
                  totalTtc: '1 250,00 €',
                  validUntil: '31/12/2025',
                  issueDate: '01/01/2026',
                  dueDate: '15/01/2026',
                  currencyCode: 'EUR',
                  iban: 'FR76 1234 5678 9012 3456 7890 123',
                  bic: 'BNPAFRPP',
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('templates.email.preview.bodyText')}</h3>
              <div className="bg-white/50 p-4 rounded-2xl border border-white/50 whitespace-pre-wrap">
                {replaceVariables(previewTemplate.bodyTemplate, {
                  quoteNumber: 'DEV-2025-001',
                  invoiceNumber: 'FAC-2025-001',
                  clientName: 'Jean Dupont',
                  companyName: 'Ma Société',
                  totalHt: '1 000,00 €',
                  totalTtc: '1 250,00 €',
                  validUntil: '31/12/2025',
                  issueDate: '01/01/2026',
                  dueDate: '15/01/2026',
                  currencyCode: 'EUR',
                  iban: 'FR76 1234 5678 9012 3456 7890 123',
                  bic: 'BNPAFRPP',
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
